import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, Button, Select, DatePicker } from "antd";
import {
  ExpandOutlined,
  ShareAltOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTags } from "hooks";
import MentionEditor from "../../common/MentionEditor";
import { SimpleTagManager } from "./SimpleTagManager";
import { toast } from "react-toastify";
import { useAuth } from "contexts/AuthContext";
import { useFileUpload } from "hooks/useFileUpload";
import { useCallback, useRef } from "react";
import {
  convertArrayRefsToFileService,
  convertFileServiceImagesToArrayRefs,
} from "utils/taskUtils";
import {
  formatNameWithEmail,
  getTaskAssigneeInfo,
  getUserDisplayName,
} from "utils/taskAssignee";
import apiClient from "utils/apiClient";
import useProcessedDescription from "hooks/useProcessedDescription";
import useWorkspaceMembers, {
  getWorkspaceMemberDisplayName,
  getWorkspaceMemberEmail,
} from "hooks/workspaces/useWorkspaceMembers";

/* ----- Enums (mirror backend) ----- */
const categories = [
  { value: "TASK", label: "Task" },
  { value: "FEATURE_REQUEST", label: "Feature Request" },
  { value: "IMPROVEMENT", label: "Improvement" },
  { value: "BUG", label: "Bug" },
];

const taskStatuses = [
  { value: "OPEN", label: "Open" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "COMPLETED", label: "Completed" },
  { value: "BLOCKED", label: "Blocked" },
  { value: "BACKLOG", label: "Backlog" },
  { value: "CANCELLED", label: "Cancelled/Abandoned" },
];

const assignmentStatuses = [
  { value: "ACCEPTED", label: "Accepted" },
  { value: "OTW", label: "On The Way" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "EXPIRED", label: "Expired" },
];

const priorities = [
  { value: 0, label: "Critcal" },
  { value: 1, label: "High" },
  { value: 2, label: "Medium" },
  { value: 3, label: "Low" },
];

const storyPoints = [1, 2, 3, 5, 8, 13].map((p) => ({
  value: p,
  label: p,
}));

const ARRAY_REF_PATTERN = /\[([0-9a-fA-F-]{36})\s*,\s*(\d+)\s*,\s*(\d+)\]/g;

const dedupeTextFilesByFileId = (files = []) => {
  if (!Array.isArray(files) || files.length === 0) return [];

  const byFileId = new Map();
  files.forEach((file) => {
    const fileId = file?.fileId;
    if (!fileId) return;

    if (!byFileId.has(fileId)) {
      byFileId.set(fileId, {
        fileId,
        width: Number(file?.width) > 0 ? Number(file.width) : 1,
        height: Number(file?.height) > 0 ? Number(file.height) : 1,
      });
    }
  });

  return [...byFileId.values()];
};

const extractArrayRefTextFiles = (html = "") => {
  if (!html) return [];

  const extracted = [];
  let match;
  while ((match = ARRAY_REF_PATTERN.exec(html)) !== null) {
    extracted.push({
      fileId: match[1],
      width: Number(match[2]) > 0 ? Number(match[2]) : 1,
      height: Number(match[3]) > 0 ? Number(match[3]) : 1,
    });
  }
  ARRAY_REF_PATTERN.lastIndex = 0;
  return dedupeTextFilesByFileId(extracted);
};

/* ----- component ----- */
const TaskFormDrawer = ({
  open,
  onClose,
  onSubmit,
  mode = "create",
  initialValues = null,
  // projectMembers,
  loading,
}) => {
  const { users: workspaceUsers, loading: usersLoading } = useWorkspaceMembers({
    enabled: open,
  });
  const { tags } = useTags();
  const { user } = useAuth();
  const { uploadFile } = useFileUpload();
  const inlineUploads = useRef({
    pending: new Map(),
    byUrl: new Map(),
    byUrlBase: new Map(),
    inFlight: 0,
    textFiles: [],
  });

  const getImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

  const fetchSignedImageUrl = useCallback(async (fileId) => {
    if (!fileId) return null;

    try {
      const response = await apiClient.get(
        `/storage-service/api/v1/files/photo/signed-urls?fids=${fileId}`,
      );
      const payload = response?.data;

      if (Array.isArray(payload) && payload.length > 0) {
        return payload[0];
      }
      if (Array.isArray(payload?.signedUrls) && payload.signedUrls.length > 0) {
        return payload.signedUrls[0];
      }
      if (typeof payload?.url === "string" && payload.url.trim()) {
        return payload.url;
      }
    } catch (error) {
      console.error("[TaskFormDrawer] failed to fetch signed image url", {
        fileId,
        error: error?.message,
      });
    }

    return null;
  }, []);

  const [isExpanded, setIsExpanded] = useState(false);
  const [localTags, setLocalTags] = useState(initialValues?.tags || []);
  const [form] = Form.useForm();
  const UNKNOWN_ASSIGNEE_VALUE = "__unknown__";
  const processedInitialDescription = useProcessedDescription(
    initialValues?.description,
    initialValues?.textFiles,
  );
  const processedInitialAcceptanceCriteria = useProcessedDescription(
    initialValues?.acceptanceCriteria,
    initialValues?.textFiles,
  );

  const getUserEmail = useCallback(
    (profile = {}) =>
      profile?.email || profile?.contactDetails?.personalEmail || "",
    [],
  );

  const getAssigneeLabel = useCallback(
    (assigneeId, fallbackName = "", fallbackEmail = "") => {
      const matchedUser = (Array.isArray(workspaceUsers) ? workspaceUsers : []).find(
        (profile) => String(profile?.id) === String(assigneeId),
      );

      if (matchedUser) {
        const email = getWorkspaceMemberEmail(matchedUser) || getUserEmail(matchedUser);
        const name =
          getUserDisplayName(matchedUser) ||
          getWorkspaceMemberDisplayName(matchedUser) ||
          "";
        return formatNameWithEmail(
          name || email || "Unknown",
          name ? email : "",
        );
      }

      const cleanName = String(fallbackName || "").trim();
      if (cleanName && cleanName !== "Unknown") {
        return formatNameWithEmail(cleanName, fallbackEmail || "");
      }

      return String(fallbackEmail || "").trim() || "Unknown";
    },
    [getUserEmail, workspaceUsers],
  );

  const seedInlineMapFromHtml = useCallback((html = "", textFiles = []) => {
    if (!html || !Array.isArray(textFiles) || textFiles.length === 0) return;

    const normalizeUrl = (url = "") => (url ? url.split("?")[0] : "");
    const imgMatches = [...html.matchAll(/<img\b[^>]*src=["']([^"']+)["'][^>]*>/gi)];
    imgMatches.forEach((match, index) => {
      const src = match[1];
      const ref = textFiles[index];
      if (!src || !ref?.fileId || src.startsWith("file-service://")) return;

      const widthMatch = match[0].match(/width=["']?(\d+)["']?/i);
      const heightMatch = match[0].match(/height=["']?(\d+)["']?/i);
      const info = {
        fileId: ref.fileId,
        width: ref.width || (widthMatch ? parseInt(widthMatch[1], 10) : 1) || 1,
        height: ref.height || (heightMatch ? parseInt(heightMatch[1], 10) : 1) || 1,
      };
      inlineUploads.current.byUrl.set(src, info);
      inlineUploads.current.byUrlBase.set(normalizeUrl(src), info);
    });
  }, []);

  useEffect(() => {
    inlineUploads.current.pending.clear();
    inlineUploads.current.byUrl.clear();
    inlineUploads.current.byUrlBase.clear();

    if (mode === "edit" && initialValues) {
      inlineUploads.current.textFiles = dedupeTextFilesByFileId(
        Array.isArray(initialValues.textFiles) ? initialValues.textFiles : [],
      );

      const init = {
        ...initialValues,
        description:
          processedInitialDescription ||
          convertArrayRefsToFileService(initialValues.description || ""),
        acceptanceCriteria:
          processedInitialAcceptanceCriteria ||
          convertArrayRefsToFileService(initialValues.acceptanceCriteria || ""),
      };
      const initialAssignee = getTaskAssigneeInfo(initialValues || {});
      if (!init.assigneeId && initialAssignee.assigneeId) {
        init.assigneeId = initialAssignee.assigneeId;
      }
      const assigneeFieldValue = init.assigneeId
        ? {
            value: init.assigneeId,
            label:
              initialAssignee.label && initialAssignee.label !== "Unknown"
                ? formatNameWithEmail(
                    initialAssignee.label,
                    initialAssignee.assigneeEmail || "",
                  )
                : initialAssignee.assigneeEmail || "Unknown",
          }
        : null;

      form.setFieldsValue({
        ...init,
        assigneeId: assigneeFieldValue,
        startedAt: initialValues.startedAt
          ? dayjs(initialValues.startedAt)
          : null,
        expectedCompletionDate: initialValues.expectedCompletionDate
          ? dayjs(initialValues.expectedCompletionDate)
          : null,
        actualCompletionDate: initialValues.actualCompletionDate
          ? dayjs(initialValues.actualCompletionDate)
          : null,
        tags: initialValues.tags?.map((tag) => tag.id) || [],
      });
      seedInlineMapFromHtml(init.description, initialValues.textFiles);
      seedInlineMapFromHtml(init.acceptanceCriteria, initialValues.textFiles);
      setLocalTags(initialValues.tags || []);
    } else if (mode === "create") {
      inlineUploads.current.textFiles = [];
      form.resetFields();
      form.setFieldsValue({
        status: "OPEN",
        priority: 2,
        points: 1,
      });
    }
  }, [
    form,
    initialValues,
    mode,
    processedInitialAcceptanceCriteria,
    processedInitialDescription,
    seedInlineMapFromHtml,
  ]);

  useEffect(() => {
    if (mode !== "edit" || !initialValues) return;
    if (form.isFieldTouched("assigneeId")) return;

    const initialAssignee = getTaskAssigneeInfo(initialValues || {});
    if (!initialAssignee.assigneeId) return;

    const currentAssigneeField = form.getFieldValue("assigneeId");
    const currentValue =
      typeof currentAssigneeField === "object"
        ? currentAssigneeField?.value
        : currentAssigneeField;
    if (String(currentValue || "") !== String(initialAssignee.assigneeId)) return;

    const nextLabel = getAssigneeLabel(
      initialAssignee.assigneeId,
      initialAssignee.label,
      initialAssignee.assigneeEmail,
    );
    const currentLabel =
      typeof currentAssigneeField === "object"
        ? String(currentAssigneeField?.label || "")
        : "";
    if (currentLabel === nextLabel) return;

    form.setFieldsValue({
      assigneeId: {
        value: initialAssignee.assigneeId,
        label: nextLabel,
      },
    });
  }, [form, getAssigneeLabel, initialValues, mode]);

  useEffect(() => {
    if (mode !== "edit" || !initialValues) return;

    if (
      processedInitialDescription &&
      !form.isFieldTouched("description") &&
      form.getFieldValue("description") !== processedInitialDescription
    ) {
      form.setFieldsValue({ description: processedInitialDescription });
      seedInlineMapFromHtml(processedInitialDescription, initialValues.textFiles);
    }

    if (
      processedInitialAcceptanceCriteria &&
      !form.isFieldTouched("acceptanceCriteria") &&
      form.getFieldValue("acceptanceCriteria") !== processedInitialAcceptanceCriteria
    ) {
      form.setFieldsValue({
        acceptanceCriteria: processedInitialAcceptanceCriteria,
      });
      seedInlineMapFromHtml(
        processedInitialAcceptanceCriteria,
        initialValues.textFiles,
      );
    }
  }, [
    form,
    initialValues,
    mode,
    processedInitialAcceptanceCriteria,
    processedInitialDescription,
    seedInlineMapFromHtml,
  ]);

  const handleFinish = async (values) => {
      const applyInlineImageReplacements = async (html = "") => {
        if (!html) return html;

      if (inlineUploads.current.inFlight > 0) {
        throw new Error("inline-uploads-in-progress");
      }

      let resultHtml = html;
      const processed = new Set();
      const normalizeUrl = (url = "") => {
        if (!url) return "";
        return url.split("?")[0];
      };

      // Replace markdown placeholders with compact array string [uuid,width,height]
      const regex = /!\[Uploading[^\]]*\]\(upload:\/\/([^)]+)\)/g;
      let match;
      while ((match = regex.exec(html)) !== null) {
        const markerId = match[1];
        const replacement = inlineUploads.current.pending.get(markerId);
        if (!replacement) {
          throw new Error("inline-upload-missing");
        }

        const { fileId, width, height } = replacement;
        const arrayString = `[${fileId}, ${width || 1}, ${height || 1}]`;
        resultHtml = resultHtml.replace(match[0], arrayString);
        console.info("[TaskFormDrawer] markdown placeholder replaced", {
          markerId,
          arrayString,
        });
        processed.add(markerId);
      }

      // Also convert escaped img tags that Quill keeps as text (e.g., &lt;img src="file-service://id" width="w" height="h" /&gt;)
      const escapedImgRegex = /&lt;img[^>]*file-service:\/\/([0-9a-fA-F-]+)[^>]*width="(\d+)"[^>]*height="(\d+)"[^>]*\/?&gt;/g;
      resultHtml = resultHtml.replace(escapedImgRegex, (_m, id, w, h) => {
        const arrayString = `[${id}, ${w || 1}, ${h || 1}]`;
        return arrayString;
      });

      // Convert rich-editor img tags (signed URLs) back to compact array refs
      resultHtml = resultHtml.replace(
        /<img\b[^>]*src=["']([^"']+)["'][^>]*>/gi,
        (imgTag, src) => {
          const normalizedSrc = normalizeUrl(src);
          const replacement =
            inlineUploads.current.byUrl.get(src) ||
            inlineUploads.current.byUrlBase.get(normalizedSrc);
          if (!replacement?.fileId) return imgTag;

          const widthMatch = imgTag.match(/width=["']?(\d+)["']?/i);
          const heightMatch = imgTag.match(/height=["']?(\d+)["']?/i);
          const width =
            replacement.width || (widthMatch ? parseInt(widthMatch[1], 10) : 1) || 1;
          const height =
            replacement.height || (heightMatch ? parseInt(heightMatch[1], 10) : 1) || 1;
          return `[${replacement.fileId}, ${width}, ${height}]`;
        },
      );

      resultHtml = convertFileServiceImagesToArrayRefs(resultHtml);

      processed.forEach((id) => inlineUploads.current.pending.delete(id));
      console.info("[TaskFormDrawer] applyInlineImageReplacements result", {
        pendingAfter: inlineUploads.current.pending.size,
        length: resultHtml?.length,
        sample: resultHtml?.slice(0, 200),
      });
      return resultHtml;
    };

    let resolvedAssigneeId = null;
    const raw = values.assigneeId;
    const initialAssigneeLabel = getTaskAssigneeInfo(initialValues || {}).label;
    const initialAssigneeName =
      initialAssigneeLabel && initialAssigneeLabel !== "Unknown"
        ? initialAssigneeLabel
        : null;
    
    if (raw) {
      if (typeof raw === "object" && raw.value) {
        const val = raw.value;
        if (val === UNKNOWN_ASSIGNEE_VALUE) {
          resolvedAssigneeId = null;
        } else {
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);
        
        if (isUuid) {
         
          resolvedAssigneeId = val;
        } else {
         
          if (initialAssigneeName && val === initialAssigneeName) {
           
          } else {
            resolvedAssigneeId = val;
          }
        }
        }
      } else if (typeof raw === "string") {
        if (raw === UNKNOWN_ASSIGNEE_VALUE) {
          resolvedAssigneeId = null;
        } else {
       
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw);
        
        if (isUuid) {
          resolvedAssigneeId = raw;
       
        } else if (initialAssigneeName && raw === initialAssigneeName) {
     
        } else {
          resolvedAssigneeId = raw;
        }
        }
      }
    }

    let finalDescription = values.description;
    let finalAcceptanceCriteria = values.acceptanceCriteria;
    try {
      finalDescription = await applyInlineImageReplacements(values.description);
      finalAcceptanceCriteria = await applyInlineImageReplacements(
        values.acceptanceCriteria,
      );
    } catch (err) {
      if (err.message === "inline-uploads-in-progress") {
        toast.error("Please wait for inline image uploads to finish.");
        return;
      }
      if (err.message === "inline-upload-missing") {
        toast.error("An inline image is still processing. Please retry in a moment.");
        return;
      }
      throw err;
    }

    inlineUploads.current.pending.clear();
    inlineUploads.current.byUrl.clear();
    inlineUploads.current.byUrlBase.clear();

    const textFilesFromContent = dedupeTextFilesByFileId([
      ...extractArrayRefTextFiles(finalDescription),
      ...extractArrayRefTextFiles(finalAcceptanceCriteria),
    ]);

    const fallbackTextFiles = dedupeTextFilesByFileId(
      inlineUploads.current.textFiles || [],
    );
    const normalizedTextFiles =
      textFilesFromContent.length > 0 ? textFilesFromContent : fallbackTextFiles;

    const initialFileIds = new Set(
      dedupeTextFilesByFileId(initialValues?.textFiles || []).map(
        (file) => file.fileId,
      ),
    );
    const textFilesForPayload =
      mode === "edit"
        ? normalizedTextFiles.filter((file) => !initialFileIds.has(file.fileId))
        : normalizedTextFiles;

    const payload = {
      type: values.type,
      title: values.title,
      description: finalDescription,
      acceptanceCriteria: finalAcceptanceCriteria,
      status: values.status,
      priority: values.priority,
      points: values.points,
      tagIds: values.tags || [],
      textFiles: textFilesForPayload,
      
      ...(resolvedAssigneeId ? { assigneeId: resolvedAssigneeId } : {}),
      startedAt: values.startedAt?.toISOString(),
      expectedCompletionDate: values.expectedCompletionDate?.toISOString(),
      actualCompletionDate: values.actualCompletionDate?.toISOString(),
    };
    console.info("[TaskFormDrawer] submit payload textFiles", {
      mode,
      totalDetected: normalizedTextFiles.length,
      initialCount: initialFileIds.size,
      sendingCount: payload.textFiles?.length || 0,
      textFiles: payload.textFiles,
    });
    
    try {
      await onSubmit?.(payload);
      if (mode === "create") {
        form.resetFields();
      }
    } catch (error) {
      toast.error(error.message || "An error occurred while saving the task");
    }
  };

  const handleFieldImagePaste = useCallback(
    async (fieldName, file, helpers) => {
      const {
        insertPlaceholder,
        replacePlaceholderWithText,
        replacePlaceholderWithImage,
        getHtml,
      } = helpers;
      const extension = file.type?.split("/")[1] || "png";
      const filename =
        file.name && file.name.trim().length > 0
          ? file.name
          : `pasted-${Date.now()}.${extension}`;
      const markerId = `upload-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const placeholderText = `Uploading ${filename}... please wait`;

      const placeholderRange = insertPlaceholder(placeholderText);
      console.info("[TaskFormDrawer] placeholder inserted", {
        markerId,
        placeholderText,
        fieldName,
      });
      form.setFieldsValue({ [fieldName]: getHtml() });
      inlineUploads.current.inFlight += 1;

      try {
        const fileForUpload = new File([file], filename, {
          type: file.type || "image/png",
        });
        console.info("[TaskFormDrawer] starting upload", {
          markerId,
          filename,
          size: fileForUpload.size,
          type: fileForUpload.type,
        });

        const uploadResult = await uploadFile(user?.id, fileForUpload, {
          purpose: "TASK_PHOTOS",
          fileType: "PHOTO",
        });
        const fileId = uploadResult?.fileId;
        console.info("[TaskFormDrawer] upload success", { markerId, fileId });

        // Compute dimensions for backend constraints (fallback to 1x1)
        let imgDims = { width: 1, height: 1 };
        try {
          imgDims = await getImageDimensions(fileForUpload);
        } catch (dimErr) {
          console.warn("[TaskFormDrawer] failed to read image dimensions, defaulting to 1x1", dimErr);
        }

        const signedUrl =
          (await fetchSignedImageUrl(fileId)) ||
          uploadResult?.meta?.url ||
          uploadResult?.url ||
          null;

        inlineUploads.current.pending.set(markerId, {
          fileId,
          alt: filename.replace(/\.[^/.]+$/, ""),
          width: imgDims.width || 1,
          height: imgDims.height || 1,
        });
        if (signedUrl) {
          inlineUploads.current.byUrl.set(signedUrl, {
            fileId,
            width: imgDims.width || 1,
            height: imgDims.height || 1,
          });
          inlineUploads.current.byUrlBase.set(signedUrl.split("?")[0], {
            fileId,
            width: imgDims.width || 1,
            height: imgDims.height || 1,
          });
        }
        const existingTextFiles = inlineUploads.current.textFiles || [];
        if (!existingTextFiles.some((item) => item?.fileId === fileId)) {
          inlineUploads.current.textFiles = [
            ...existingTextFiles,
            { fileId, width: imgDims.width || 1, height: imgDims.height || 1 },
          ];
        }

        // Show the actual image immediately when signed URL is available.
        if (signedUrl) {
          replacePlaceholderWithImage(placeholderRange, signedUrl);
        } else {
          replacePlaceholderWithText(placeholderRange, "(image uploaded; preview unavailable)");
        }
        const htmlAfterReplace = getHtml();
        console.info("[TaskFormDrawer] placeholder replaced", {
          markerId,
          hasSignedUrl: !!signedUrl,
          htmlSample: htmlAfterReplace?.slice(0, 200),
        });
        form.setFieldsValue({ [fieldName]: htmlAfterReplace });
      } catch (err) {
        replacePlaceholderWithText(placeholderRange, "(image upload failed)");
        toast.error("Failed to upload pasted image. Please try again.");
        console.error("[TaskFormDrawer] image paste flow failed", {
          markerId,
          error: err?.message,
        });
      } finally {
        inlineUploads.current.inFlight = Math.max(
          0,
          inlineUploads.current.inFlight - 1
        );
      }
    },
    [fetchSignedImageUrl, form, uploadFile, user?.id]
  );

  const handleDescriptionImagePaste = useCallback(
    (file, helpers) => handleFieldImagePaste("description", file, helpers),
    [handleFieldImagePaste],
  );

  const handleAcceptanceCriteriaImagePaste = useCallback(
    (file, helpers) =>
      handleFieldImagePaste("acceptanceCriteria", file, helpers),
    [handleFieldImagePaste],
  );

  const handleClose = () => {
    inlineUploads.current.pending.clear();
    inlineUploads.current.byUrl.clear();
    inlineUploads.current.byUrlBase.clear();
    inlineUploads.current.textFiles = [];
    form.resetFields();
    setLocalTags([]);
    onClose?.();
  };


  const assigneeOptions = [
    { value: UNKNOWN_ASSIGNEE_VALUE, label: "Unknown" },
    ...(Array.isArray(workspaceUsers) ? workspaceUsers : []).map((user) => ({
      value: user.id,
      label: formatNameWithEmail(
        getUserDisplayName(user) ||
          getWorkspaceMemberDisplayName(user) ||
          String(user.id),
        getWorkspaceMemberEmail(user),
      ),
    })),
  ];
  const mentionUsers = (Array.isArray(workspaceUsers) ? workspaceUsers : [])
    .filter((u) => u && u.id)
    .map((u) => ({
      id: String(u.id),
      name: getWorkspaceMemberDisplayName(u),
      email: getWorkspaceMemberEmail(u),
    }));

  return (
    <Drawer
      size={isExpanded ? 520 : "100vw"}
      zIndex={9999}
      open={open}
      onClose={handleClose}
      destroyOnHidden
      closable={false}
      footer={
        <div
          style={{
            textAlign: "right",
            maxWidth: isExpanded ? "100vw" : 700,
            margin: isExpanded ? 0 : "0 auto",
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <Button style={{ marginRight: 8 }} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={loading}
          >
            {mode === "edit" ? "Save Changes" : "Create"}
          </Button>
        </div>
      }
    >
      <div
        style={{
          maxWidth: isExpanded ? "100vw" : 700,
          margin: isExpanded ? 0 : "0 auto",
          fontFamily: "Inter, san-serif",
        }}
      >
        {/* Breadcrumb / page context */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            <span style={{ cursor: "pointer" }}>Project</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>
              {mode === "edit" ? "Edit Task" : "Create Task"}
            </span>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <ExpandOutlined
              className="task-drawer-icon"
              onClick={() => setIsExpanded((prev) => !prev)}
              style={{ cursor: "pointer" }}
            />
            <ShareAltOutlined className="task-drawer-icon" />
            <MoreOutlined className="task-drawer-icon" />
          </div>
        </div>

        <Form layout="vertical" form={form} onFinish={handleFinish}>
          {/* category */}
          <Form.Item name="type" label="Category" rules={[{ required: true }]}>
            <Select
              options={categories}
              allowClear
              placeholder="Select category"
            />
          </Form.Item>

          {/* title */}
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Title is required" }]}
          >
            <Input placeholder="Task title" />
          </Form.Item>

          {/* description */}
          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Description is required" }]}
            style={{ marginBottom: 10 }}
          >
            <MentionEditor
              value={form.getFieldValue("description") || ""}
              onChange={(html) => form.setFieldsValue({ description: html })}
              users={mentionUsers}
              height="150px"
              placeholder="Write task description..."
              onImagePaste={handleDescriptionImagePaste}
              debugId="TaskFormDrawer:Description"
            />
          </Form.Item>

          {/* Acceptance Criteria */}
          <Form.Item
            name="acceptanceCriteria"
            label="Acceptance Criteria"
            rules={[
              { required: true, message: "Acceptance criteria is required" },
            ]}
            style={{ marginBottom: 10 }}
          >
            <MentionEditor
              value={form.getFieldValue("acceptanceCriteria") || ""}
              onChange={(html) =>
                form.setFieldsValue({ acceptanceCriteria: html })
              }
              users={mentionUsers}
              height="150px"
              placeholder="Write the acceptance criteria..."
              onImagePaste={handleAcceptanceCriteriaImagePaste}
              debugId="TaskFormDrawer:AcceptanceCriteria"
            />
          </Form.Item>

          {/* Status */}
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select options={taskStatuses} placeholder="Status" />
          </Form.Item>

          {/* Assignment Status */}
          <Form.Item name="assignmentStatus" label="Assignment Status">
            <Select
              options={assignmentStatuses}
              allowClear
              placeholder="Assignment status"
            />
          </Form.Item>

          {/* Assignee */}
          <Form.Item name="assigneeId" label="Assignee">
            <Select
              allowClear
              placeholder="Search and select assignee"
              loading={usersLoading}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              options={assigneeOptions}
              labelInValue
              virtual={false}
              getPopupContainer={(trigger) => trigger.parentElement}
            />
          </Form.Item>

          {/* Priority */}
          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true }]}
          >
            <Select options={priorities} placeholder="Select priority" />
          </Form.Item>

          {/* Story Points */}
          <Form.Item name="points" label="Points" rules={[{ required: true }]}>
            <Select options={storyPoints} placeholder="Select points" />
          </Form.Item>

          {/* Dates */}
          <Form.Item
            name="startedAt"
            label="Started At"
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="expectedCompletionDate"
            label="Expected Completion Date"
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="actualCompletionDate" label="Actual Completion Date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          {/* Tags */}
          {mode === "edit" && (
            <>
              <label style={{ color: "#555" }}>Tags</label>
              <SimpleTagManager
                entityType="task"
                entityId={initialValues?.id}
                projectId={initialValues?.projectId}
                currentTags={localTags}
                availableTags={tags}
                onChange={(updatedTags) => {
                  setLocalTags(updatedTags);
                }}
              />
            </>
          )}
        </Form>
      </div>
    </Drawer>
  );
};

export default TaskFormDrawer;
